import { normalizedLines, stockLines } from "@lagerlog/core";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { App } from "./App.tsx";
import { applyBrand } from "./brand.ts";
import { store } from "./store.ts";
import { installSuggestions } from "./suggest.ts";
import "./styles.css";
import { update } from "./update.ts";

const apply = registerSW({
  onNeedRefresh() {
    update.ready = true;
    window.dispatchEvent(new Event("lagerlog:update"));
  },
  // The browser looks for a new version only when the page loads. An installed app on the iPhone
  // comes back from the background without loading, often for days: so look again whenever it is
  // brought to the front, and every half hour while it is open.
  onRegisteredSW(_url, registration) {
    if (!registration) return;
    const check = () => {
      if (navigator.onLine && !update.ready) registration.update().catch(() => undefined);
    };
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && check());
    setInterval(check, 30 * 60_000);
  },
});
update.apply = () => void apply(true);

applyBrand();
installSuggestions();
createRoot(document.getElementById("app")!).render(<App />);
void store.init();

// For the browser tests and measurements (spike): read-only view on the device's state.
(window as unknown as { lagerlogDebug: unknown }).lagerlogDebug = {
  lines: () => (store.replica ? normalizedLines(stockLines(store.replica.state())) : []),
  pending: () => store.replica?.pending.length ?? -1,
  status: () => store.status,
  bookings: () => store.replica?.journal().length ?? 0,
  /** Reads a barcode from an image with zxing-wasm (as on the iPhone). */
  scan: async (img: ImageData) => (await (await import("./scan.ts")).zxingDetector()).detect(img),
  /** The article with a card number (the demo wine list has one for each wine). */
  articleOf: (listNo: string) => [...(store.replica?.entities.values() ?? [])].find((e) => e.type === "article" && e.fields.listNo === listNo)?.id ?? "",
  /** The location in the device's storages where most of an article is. */
  locationOf: (article: string) => {
    const state = store.replica?.state();
    let best = "";
    let most = -Infinity;
    for (const acc of state?.accounts.values() ?? []) {
      if (acc.article === article && acc.qty > most) [best, most] = [acc.location, acc.qty];
    }
    return best;
  },
};
